<script setup lang="ts">
import { Drawer, Button, Select, Fluid } from 'primevue';
import ColorPickerField from 'shared/forms/components/ColorPickerField.vue';
import { storeToRefs } from 'pinia';
import RectanglePanel from './RectanglePanel.vue';
import { useSelectedShapeStore } from '../stores/selectedShapeStore';
import { computed } from 'vue';
import StorageAreaPanel from '@/map/components/StorageAreaPanel.vue';
import { useSelectedStorageAreaStore } from '../stores/selectedStorageAreaStore';
import { useActionFailedAlert } from 'shared/problems/composables/actionFailedAlert';
import { useDeleteStorageAreaConfirm } from '@/storageAreas/composables/deleteStorageAreaConfirm';
import { useDeleteShape, type DeleteShapeRequest } from '@/shapes/api/deleteShape';
import { Assert } from 'shared/assert';
import { useCreateShape, type CreateShapeRequest } from '@/shapes/api/createShape';
import { useUpdateShape, type UpdateShapeRequest } from '@/shapes/api/updateShape';
import { useUpdateStorageArea, type UpdateStorageAreaRequest } from '@/storageAreas/api/updateStorageArea';
import { useSelectedLogYardStore } from '../stores/selectedLogYardStore';
import { useCreateStorageArea, type CreateStorageAreaRequest } from '@/storageAreas/api/createStorageArea';
import { useEditInventoryStore } from '@/storageAreas/logYards/stores/editInventoryStore';
import { ShapeMode } from 'shared/shapes/domain/shapeMode';
import PolygonPanel from './PolygonPanel.vue';
import { useSelectedLogSorterStore } from '../stores/selectedLogSorterStore';
import { useQueryLogSorter } from '@/logSorter/api/queryLogSorter';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { useUndoStore, type AddUndoOptions } from 'shared/undo/stores/undoStore';
import { useDeleteStorageArea, type DeleteStorageAreaRequest } from '@/storageAreas/api/deleteStorageArea';
import { useUndoDeleteShape, type UndoDeleteShapeRequest } from '@/shapes/api/undoDeleteShape';
import { useSelectedCoilStackStore } from '../stores/selectedCoilStackStore';
import { useAuthStore } from '@/auth/store/authStore';
import { isBulkStorage, isLogYard, StorageAreaType } from 'shared/storageAreas/domain/storageAreaType';
import EditProductionOrderDialog from '@/productionOrders/components/EditProductionOrderDialog.vue';
import { templateRef } from '@vueuse/core';
import { getShapeId } from 'shared/shapes/domain/shapeGeoJson';
import { useShapeStore } from 'shared/shapes/stores/shapeStore';
import { useStorageAreaStore } from 'shared/storageAreas/stores/storageAreaStore';
import { useSelectedBulkStore } from '../stores/selectedBulkStore';
import { useEditBulkInventoryStore } from '@/storageAreas/bulk/stores/editBulkInventoryStore';

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const undoStore = useUndoStore();

const selectedShapeStore = useSelectedShapeStore();
const selectedStorageAreaStore = useSelectedStorageAreaStore();
const selectedLogYardStore = useSelectedLogYardStore();
const selectedBulkStore = useSelectedBulkStore();
const selectedCoilStackStore = useSelectedCoilStackStore();
const shapeStore = useShapeStore();
const storageAreaStore = useStorageAreaStore();
const actionFailedAlert = useActionFailedAlert();
const deleteStorageAreaConfirm = useDeleteStorageAreaConfirm();
const editInventoryStore = useEditInventoryStore();
const editBulkInventoryStore = useEditBulkInventoryStore();
const logSorterStore = useSelectedLogSorterStore();
const authStore = useAuthStore();
const editProductionOrderDialog = templateRef('editProductionOrderDialog');

const createShape = useCreateShape();
const updateShape = useUpdateShape();
const deleteShape = useDeleteShape();
const undoDeleteShape = useUndoDeleteShape();

const queryLogSorter = useQueryLogSorter();

const createStorageArea = useCreateStorageArea();
const updateStorageArea = useUpdateStorageArea();
const deleteStorageArea = useDeleteStorageArea();

const modes = [
  ShapeMode.Rectangle,
  ShapeMode.Polygon
];

const modeOptions: ShapeModeOption[] = modes.map(t => {
  return {
    text: translations.value.enums.shapeMode(t),
    value: t
  };
});

const {
  showUI,
  name,
  shapeChanged,
  operation,
  activeTab,
  hasStorageArea,
  mode,
  hasLogSorter,
  color,
  colorChanged,
  colorEnabled,
  editMultiple
} = storeToRefs(selectedShapeStore);

const {
  storageAreaChanged,
  storageAreaInvalid,
  type,
  storageAreaId,
  operation: storageAreaOperation
} = storeToRefs(selectedStorageAreaStore);

const changed = computed(() => shapeChanged.value || storageAreaChanged.value);
const invalid = computed(() => storageAreaInvalid.value);

const deleteVisible = computed(() => operation.value === 'edit' && authStore.isAdmin);
const saveDisabled = computed(() => !changed.value || invalid.value);
const inventoryVisible = computed(() => storageAreaOperation.value === 'edit');

defineExpose({
  save,
  saveDisabled,
  changed,
  invalid
});

const {
  severity: storageAreaTabSeverity,
  variant: storageAreaTabVariant,
} = tabState('storageArea');
const storageAreaTabVisible = computed(() => hasStorageArea.value);

const {
  severity: shapeTabSeverity,
  variant: shapeTabVariant
} = tabState('shape');
const shapeTabVisible = computed(() => storageAreaTabVisible.value && authStore.isAdmin && type.value != StorageAreaType.LogSorterBin);

const isIntake = computed(() => {
  return type.value === StorageAreaType.LogSorterIntake || type.value === StorageAreaType.SawLineIntake;
});

async function showProductionOrder() {
  const id = storageAreaId.value;
  Assert.truthy(id, 'Storage area id missing, this should not happen!');
  await editProductionOrderDialog.value?.show({
    storageAreaId: id
  });
}

interface ShapeModeOption {
  value: ShapeMode;
  text: string;
}

async function saveCreate() {
  let storageAreaId: string | null = null;
  let addUndoShape = true;

  const {
    type, status, active, temporary, name, operation, note, importantNote
  } = selectedStorageAreaStore;

  if (operation !== 'none') {
    Assert.definied(active);
    Assert.definied(temporary);
    addUndoShape = false;

const {
  operation: logYardOperation,
  allowMultipleProducts: logYardAllowMultipleProducts,
  allowedProducts: logYardAllowedProducts,
  automaticProductChange: logYardAutomaticProductChange,
  sideArea
} = selectedLogYardStore;

const {
  operation: bulkStorageOperation,
  allowMultipleProducts: bulkAllowMultipleProducts,
  allowedProducts: bulkAllowedProducts,
  automaticProductChange: bulkAutomaticProductChange,
  maxWeight,
  maxVolume,

} = selectedBulkStore;

    const request: CreateStorageAreaRequest = {
      storageArea: {
        name,
        type,
        status,
        version: crypto.randomUUID(),
        active,
        temporary,
        allowedProducts: [],
        logYardData: null,
        bulkStorageData: null,
        coilStack: null,
        note: note,
        importantNote: importantNote
      }
    };

    if (logYardOperation !== 'none') {
      request.storageArea.allowedProducts = logYardAllowMultipleProducts ? logYardAllowedProducts.map(p => p.id) : logYardAllowedProducts.length > 0 ? [logYardAllowedProducts.map(p => p.id)[0]!] : []
      request.storageArea.logYardData = {
        allowMultipleProducts: logYardAllowMultipleProducts,
        disableAutomaticProductChange: !logYardAutomaticProductChange,
        sideArea: sideArea
      };
    }

    if (bulkStorageOperation !== 'none') {
      request.storageArea.allowedProducts = bulkAllowMultipleProducts ? bulkAllowedProducts.map(p => p.id) : bulkAllowedProducts.length > 0 ? [bulkAllowedProducts.map(p => p.id)[0]!] : []
      request.storageArea.bulkStorageData = {
        allowMultipleProducts: bulkAllowMultipleProducts,
        disableAutomaticProductChange: !bulkAutomaticProductChange,
        maxWeight,
        maxVolume,
      };
    }

    const {
      operation: coilStackOperation,
      slots,
      slotWidth,
      maxHeight,
      maxLevels,
      maxCoilDiameter
    } = selectedCoilStackStore;

    if (coilStackOperation !== 'none') {
      request.storageArea.coilStack = {
        slots,
        slotWidth,
        maxHeight,
        maxLevels,
        maxCoilDiameter
      };
    }

    const response = await createStorageArea.execute(request);

    if (!response.ok) {
      actionFailedAlert.show(response.details);
      return;
    }

    const deleteRequest: DeleteStorageAreaRequest = {
      storageAreaId: response.storageArea.id
    };

    undoStore.add({
      title: translations.value.undo.createStorageAreaTitle,
      description: translations.value.undo.getUndoCreateStorageAreaDescription(name),
      function: async () => {

        const response = await deleteStorageArea.execute(deleteRequest);

        if (!response.ok) {
          actionFailedAlert.show(response.details);
          return;
        }
      }
    });

    storageAreaId = response.storageArea.id;
  }

  const { geoJson } = selectedShapeStore;
  Assert.definied(geoJson);

  const request: CreateShapeRequest = {
    shape: {
      geofenceGeoJson: geoJson.geofence,
      shapeGeoJson: geoJson.shape,
      version: crypto.randomUUID(),
      storageAreaId,
      color: color.value
    }
  };

  const response = await createShape.execute(request);

  if (!response.ok) {
    actionFailedAlert.show(response.details);
    return;
  }

  if (addUndoShape) {

    const deleteRequest: DeleteShapeRequest = {
      shapeId: response.shape.id
    };

    undoStore.add({
      title: translations.value.undo.createShape.title,
      description: translations.value.undo.createShape.description,
      function: async () => {
        const response = await deleteShape.execute(deleteRequest);
        if (!response.ok) {
          actionFailedAlert.show(response.details);
          return;
        }
      }
    });
  }

  await selectedShapeStore.edit({
    shapeId: response.shape.id,
    multiple: false
  });
}

async function saveEdit() {
  const { selectedShapeIds, allUpdatedShapes, shapeChanged } = selectedShapeStore;
  const { storageAreaId, storageAreaChanged } = selectedStorageAreaStore;

  Assert.definied(selectedShapeIds);
  let success = true;

  let undo: AddUndoOptions | null = null;
  const undoFunctions: Array<() => Promise<void>> = [];

  if (shapeChanged) {
    // Update all shapes that have been edited
    for (const updatedShape of allUpdatedShapes) {
      const shapeId = getShapeId(updatedShape.shape);
      if (!shapeId) continue;

      const originalShape = shapeStore.getById(shapeId);
      if (!originalShape) continue;

      const response = await updateShape.execute({
        shapeId: shapeId,
        shape: {
          version: originalShape.version,
          geofenceGeoJson: updatedShape.geofence,
          shapeGeoJson: updatedShape.shape,
          storageAreaId: storageAreaId ?? originalShape.storageAreaId,
          color: color.value,
        },
      });

      if (!response.ok) {
        actionFailedAlert.show(response.details);
        success = false;
        break;
      }

      // Create undo operation for this shape
      const undoRequest: UpdateShapeRequest = {
        shapeId: originalShape.id,
        shape: {
          geofenceGeoJson: originalShape.geofenceGeoJson,
          shapeGeoJson: originalShape.shapeGeoJson,
          storageAreaId: originalShape.storageAreaId,
          version: response.shape.version,
          undo: true,
          color: originalShape.color,
        },
      };

      undoFunctions.push(async () => {
        const undoResponse = await updateShape.execute(undoRequest);
        if (!undoResponse.ok) {
          actionFailedAlert.show(undoResponse.details);
        }
      });
    }

    if (undoFunctions.length > 0) {
      undo = {
        title: translations.value.undo.updateShape.title,
        description:
          selectedShapeIds.length > 1
            ? translations.value.undo.updateShape.description + ` (${selectedShapeIds.length})`
            : translations.value.undo.updateShape.description,
        function: undoFunctions[0]!,
        extraFunctions: undoFunctions.slice(1),
      };
    }
  }

  if (storageAreaId !== undefined && storageAreaChanged) {

    const {
      type,
      status,
      version,
      active,
      temporary,
      name,
      currentStorageArea,
      note,
      importantNote,
      selectedStorageAreaIds,
      editMultiple: editMultipleStorageAreas,
      nameEnabled,
      typeEnabled,
      statusEnabled,
      activeEnabled,
      noteEnabled,
      importantNoteEnabled,
    } = selectedStorageAreaStore;

    const {
      operation: logYardOperation,
      allowMultipleProducts: logYardAllowMultipleProducts,
      allowedProducts: logYardAllowedProducts,
      automaticProductChange: logYardAutomaticProductChange,
      sideArea,
      allowMultipleProductsEnabled: logYardAllowMultipleProductsEnabled,
      allowedProductsEnabled: logYardAllowedProductsEnabled,
      automaticProductChangeEnabled: logYardAutomaticProductChangeEnabled,
      sideAreaEnabled
    } = selectedLogYardStore;

    const {
      operation: bulkStorageOperation,
      allowMultipleProducts: bulkAllowMultipleProducts,
      allowMultipleProductsEnabled: bulkAllowMultipleProductsEnabled,
      allowedProducts: bulkAllowedProducts,
      allowedProductsEnabled: bulkAllowedProductsEnabled,
      automaticProductChange: bulkAutomaticProductChange,
      automaticProductChangeEnabled: bulkAutomaticProductChangeEnabled,
      maxWeight,
      maxWeightEnabled,
      maxVolume,
      maxVolumeEnabled
    } = selectedBulkStore;

    const {
      operation: coilStackOperation,
      slots,
      slotWidth,
      maxHeight,
      maxLevels,
      maxCoilDiameter
    } = selectedCoilStackStore;

    // Get list of storage area IDs to update
    const storageAreaIdsToUpdate = editMultipleStorageAreas ? selectedStorageAreaIds : [storageAreaId];

    // Update each storage area
    for (const saId of storageAreaIdsToUpdate) {
      const originalStorageArea = storageAreaStore.getById(saId);
      if (!originalStorageArea) continue;

      Assert.definied(active);
      Assert.definied(temporary);

      const request: UpdateStorageAreaRequest = {
        id: saId,
        storageArea: {
          name: nameEnabled ? name : originalStorageArea.name,
          type: typeEnabled ? type : originalStorageArea.type,
          status: statusEnabled ? status : originalStorageArea.status,
          version: originalStorageArea.version,
          active: activeEnabled ? active : originalStorageArea.active,
          temporary,
          allowedProducts: [],
          logYardData: null,
          bulkStorageData: null,
          coilStack: null,
          note: noteEnabled ? note : (originalStorageArea.note ?? ''),
          importantNote: importantNoteEnabled ? importantNote : (originalStorageArea.importantNote ?? false)
        }
      };

      if (logYardOperation !== 'none' && typeEnabled) {

        const useAllowMultipleProducts = editMultipleStorageAreas ? logYardAllowMultipleProductsEnabled : true;
        const useAllowedProducts = editMultipleStorageAreas ? logYardAllowedProductsEnabled : true;
        const useAutomaticProductChange = editMultipleStorageAreas ? logYardAutomaticProductChangeEnabled : true;
        const useSideArea = editMultipleStorageAreas ? sideAreaEnabled : true;

        request.storageArea.logYardData = {
          allowMultipleProducts: useAllowMultipleProducts ? logYardAllowMultipleProducts : originalStorageArea.logYardData?.allowMultipleProducts ?? false,
          disableAutomaticProductChange: useAutomaticProductChange ? !logYardAutomaticProductChange : originalStorageArea.logYardData?.disableAutomaticProductChange ?? false,
          sideArea: useSideArea ? sideArea : originalStorageArea.logYardData?.sideArea ?? 0
        };

        if (useAllowedProducts) {
          request.storageArea.allowedProducts = logYardAllowMultipleProducts ? logYardAllowedProducts.map(p => p.id) : logYardAllowedProducts.length > 0 ? [logYardAllowedProducts.map(p => p.id)[0]!] : [];
        } else {
          request.storageArea.allowedProducts = originalStorageArea.logYardData?.allowedProducts ?? [];
        }
      } else if (originalStorageArea.logYardData) {
        request.storageArea.logYardData = {
          allowMultipleProducts: originalStorageArea.logYardData.allowMultipleProducts,
          disableAutomaticProductChange: originalStorageArea.logYardData.disableAutomaticProductChange,
          sideArea: originalStorageArea.logYardData.sideArea
        };
        request.storageArea.allowedProducts = originalStorageArea.logYardData.allowedProducts;
      }

      if (bulkStorageOperation !== 'none' && typeEnabled) {

        const useAllowMultipleProducts = editMultipleStorageAreas ? bulkAllowMultipleProductsEnabled : true;
        const useAllowedProducts = editMultipleStorageAreas ? bulkAllowedProductsEnabled : true;
        const useAutomaticProductChange = editMultipleStorageAreas ? bulkAutomaticProductChangeEnabled : true;

        const useMaxWeight = editMultipleStorageAreas ? maxWeight : true;
        const useMaxVolume = editMultipleStorageAreas ? maxVolumeEnabled : true;

        request.storageArea.bulkStorageData = {
          allowMultipleProducts: useAllowMultipleProducts ? bulkAllowMultipleProducts : originalStorageArea.bulkStorageData?.allowMultipleProducts ?? false,
          disableAutomaticProductChange: useAutomaticProductChange ? !bulkAutomaticProductChange : originalStorageArea.bulkStorageData?.disableAutomaticProductChange ?? false,
          maxVolume: useMaxVolume ? maxVolume : originalStorageArea.bulkStorageData?.maxVolume ?? null,
          maxWeight: useMaxWeight ? maxWeight : originalStorageArea.bulkStorageData?.maxWeight ?? null,
        };

        if (useAllowedProducts) {
          request.storageArea.allowedProducts = bulkAllowMultipleProducts ? bulkAllowedProducts.map(p => p.id) : bulkAllowedProducts.length > 0 ? [bulkAllowedProducts.map(p => p.id)[0]!] : [];
        } else {
          request.storageArea.allowedProducts = originalStorageArea.bulkStorageData?.allowedProducts ?? [];
        }
      } else if (originalStorageArea.bulkStorageData) {
        request.storageArea.bulkStorageData = {
          allowMultipleProducts: originalStorageArea.bulkStorageData.allowMultipleProducts,
          disableAutomaticProductChange: originalStorageArea.bulkStorageData.disableAutomaticProductChange,
          maxVolume: originalStorageArea.bulkStorageData.maxVolume,
          maxWeight: originalStorageArea.bulkStorageData.maxWeight,
        };
        request.storageArea.allowedProducts = originalStorageArea.bulkStorageData.allowedProducts;
      }

      if (coilStackOperation !== 'none' && typeEnabled) {
        request.storageArea.coilStack = {
          slots,
          slotWidth,
          maxHeight,
          maxLevels,
          maxCoilDiameter
        };
      } else if (originalStorageArea.coilStack) {
        request.storageArea.coilStack = {
          slots: originalStorageArea.coilStack.slots,
          slotWidth: originalStorageArea.coilStack.slotWidth,
          maxHeight: originalStorageArea.coilStack.maxHeight,
          maxLevels: originalStorageArea.coilStack.maxLevels,
          maxCoilDiameter: originalStorageArea.coilStack.maxCoilDiameter
        };
      }

      const response = await updateStorageArea.execute(request);

      if (!response.ok) {
        actionFailedAlert.show(response.details);
        success = false;
        break;
      }

      // Create undo operation for this storage area
      const undoRequest: UpdateStorageAreaRequest = {
        id: originalStorageArea.id,
        storageArea: {
          active: originalStorageArea.active,
          allowedProducts: [],
          logYardData: null,
          bulkStorageData: null,
          coilStack: null,
          name: originalStorageArea.name,
          status: originalStorageArea.status,
          temporary: originalStorageArea.temporary,
          type: originalStorageArea.type,
          version: response.storageArea.version,
          undo: true,
          note: originalStorageArea.note,
          importantNote: originalStorageArea.importantNote
        }
      };

      const logYard = originalStorageArea.logYardData;

      if (logYard) {
        undoRequest.storageArea.allowedProducts = logYard.allowedProducts;
        undoRequest.storageArea.logYardData = {
          allowMultipleProducts: logYard.allowMultipleProducts,
          disableAutomaticProductChange: logYard.disableAutomaticProductChange,
          sideArea: logYard.sideArea
        };
      }

      const coilStack = originalStorageArea.coilStack;

      if (coilStack) {
        undoRequest.storageArea.coilStack = {
          slots: coilStack.slots,
          maxHeight: coilStack.maxHeight,
          maxLevels: coilStack.maxLevels,
          slotWidth: coilStack.slotWidth,
          maxCoilDiameter: coilStack.maxCoilDiameter
        };
      }

      const undoTitle = translations.value.undo.editStorageAreaTitle;
      const undoDescription = editMultipleStorageAreas
        ? translations.value.undo.getUndoEditStorageAreaDescription(name) + ` (${storageAreaIdsToUpdate.length})`
        : translations.value.undo.getUndoEditStorageAreaDescription(name);

      const undoFunction = async () => {
        const response = await updateStorageArea.execute(undoRequest);

        if (!response.ok) {
          actionFailedAlert.show(response.details);
          return;
        }
      };

      if (undo) {
        undo.title = undoTitle;
        undo.description = undoDescription;
        // Merge storage area undo with existing shape undos
        if (undo.extraFunctions) {
          undo.extraFunctions.push(undoFunction);
        } else {
          undo.extraFunctions = [undoFunction];
        }
      } else {
        undo = {
          title: undoTitle,
          description: undoDescription,
          function: undoFunction,
        };
      }
    }
  }

  if (undo) {
    undoStore.add(undo);
  }

  if (success) {
    selectedShapeStore.deselect(null, true);

    for (const updatedShape of allUpdatedShapes) {
      const shapeId = getShapeId(updatedShape.shape);
      if (!shapeId) continue;

      await selectedShapeStore.edit({
        shapeId,
        multiple: allUpdatedShapes.length > 1,
      });
    }
  }
}

async function save() {
  const { operation } = selectedShapeStore;

  if (operation === 'create') {
    await saveCreate();
  } else if (operation === 'edit') {
    await saveEdit();
  }
}

async function onDeleteClick() {

  const {
    selectedShapeIds
  } = selectedShapeStore;

  const {
    storageAreaId
  } = selectedStorageAreaStore;

  if (storageAreaId) {

    await deleteStorageAreaConfirm.show({
      storageAreaId
    });

  } else if (selectedShapeIds) {

    const removeShapeResponse = await deleteShape.execute({
      shapeId: selectedShapeIds[0]!,
    });

    if (!removeShapeResponse.ok) {
      actionFailedAlert.show(removeShapeResponse.details);
    } else {

      const undoRequest: UndoDeleteShapeRequest = {
        shapeId: selectedShapeIds[0]!,
      };

      undoStore.add({
        title: translations.value.undo.deleteShape.title,
        description: translations.value.undo.deleteShape.description,
        function: async () => {
          const response = await undoDeleteShape.execute(undoRequest);
          if (!response.ok) {
            actionFailedAlert.show(response.details);
            return;
          }
        }
      })
    }
  }

  selectedShapeStore.deselect(null);
}

async function onEditInventoryClick() {
  const { storageAreaId, type } = selectedStorageAreaStore;
  Assert.definied(storageAreaId);

  if (isLogYard(type)) {
    await editInventoryStore.show({
      storageAreaId: storageAreaId
    });
    return;
  }

  if (isBulkStorage(type)) {
    await editBulkInventoryStore.show({
      storageAreaId: storageAreaId
    });
    return;
  }
}

function tabState(value: string) {
  return {
    severity: tabSeverity(value),
    variant: tabVariant(value)
  };
}

function tabSeverity(value: string) {
  return computed(() => activeTab.value === value ? 'primary' : undefined);
}

function tabVariant(value: string) {
  return computed(() => activeTab.value !== value ? 'outlined' : undefined);
}

async function onOpenLogSorter() {

  const storageAreaId = selectedStorageAreaStore.storageAreaId;

  const response = await queryLogSorter.execute({
    binId: storageAreaId
  });

  if (!response.ok) {
    actionFailedAlert.show(response.details);
    return;
  }

  selectedShapeStore.deselect(null);

  logSorterStore.edit({
    logSorter: response.logSorter
  });
}

</script>

<template>
  <Drawer v-model:visible="showUI" :header="name" position="right" :modal="false" :dismissable="false"
    @before-hide="selectedShapeStore.deselect(null)" class="shape-drawer w-25">

    <div class="tabs">
      <Button :hidden="!storageAreaTabVisible" :severity="storageAreaTabSeverity" :variant="storageAreaTabVariant"
        @click="activeTab = 'storageArea'">{{ translations.generic.storageArea
        }}</Button>
      <Button :hidden="!shapeTabVisible" :severity="shapeTabSeverity" :variant="shapeTabVariant"
        @click="activeTab = 'shape'">{{ translations.generic.shape }}</Button>
    </div>

    <div class="body">
      <StorageAreaPanel v-if="activeTab === 'storageArea'"></StorageAreaPanel>
      <div v-if="activeTab === 'shape'">
        <ColorPickerField class="mb-4" :changed="colorChanged" v-model:model-value="color" v-if="colorEnabled">
        </ColorPickerField>
        <Select class="mb-4" fluid v-model:model-value="mode" :options="modeOptions" option-value="value"
          option-label="text" @change="selectedShapeStore.modeChanged()" v-if="!editMultiple"></Select>
        <RectanglePanel v-if="mode === ShapeMode.Rectangle && !editMultiple"></RectanglePanel>
        <PolygonPanel v-if="mode === ShapeMode.Polygon" && !editMultiple></PolygonPanel>
      </div>
    </div>

    <div class="actions">
      <Fluid class="grid grid-cols-2 gap-2 w-full">
        <Button v-if="hasLogSorter && authStore.isAdmin" icon="pi pi-sort-amount-up"
          :label="translations.generic.logSorter" class="col-span-2" @click="onOpenLogSorter"></Button>

        <Button v-if="isIntake" :label="translations.generic.productionOrder" icon="pi pi-barcode"
          @click="showProductionOrder" class="col-span-2"></Button>

        <Button :disabled="saveDisabled" class="save-button" :label="translations.generic.save" icon="pi pi-save"
          @click="save"></Button>

        <Button v-if="inventoryVisible" icon="pi pi-search" :label="translations.generic.inventory"
          @click="onEditInventoryClick"></Button>

        <Button v-if="deleteVisible" severity="danger" icon="pi pi-trash" :label="translations.generic.delete"
          @click="onDeleteClick"></Button>
      </Fluid>
    </div>

  </Drawer>

  <EditProductionOrderDialog ref="editProductionOrderDialog"></EditProductionOrderDialog>
</template>

<style scoped>
.tabs {
  display: flex;
  column-gap: .5rem;
}

.actions {
  display: flex;
  flex-direction: row;
  row-gap: 1rem;
  column-gap: 1rem;
  justify-content: start;
}
</style>

<style>
.shape-drawer {
  width: 30rem !important;
}

.shape-drawer .p-drawer-content {
  row-gap: .5rem;
  display: grid;
  grid-template-rows: auto 1fr auto auto;
}
</style>
